import React, { useCallback, useEffect, useState } from "react";
import { Search, Plus, Minus, Loader2, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { Card, Badge, Empty, inputCls, btnPrimary, gradient } from "./ui";

export function useUsers() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const load = useCallback(async (query = q) => {
    try { const { data } = await api.get("/admin/users", { params: { q: query || undefined } }); setUsers(data.users); }
    catch { toast.error("Couldn't load users."); }
  }, [q]);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  return { q, setQ, users, reload: () => load(q) };
}

export function SearchBox({ value, onChange, testid }) {
  return (
    <div className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Search by email or name…" className={`${inputCls} w-full pl-9`} />
    </div>
  );
}

function AdjustForm({ user, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (sign) => {
    const n = Math.abs(parseInt(amount, 10));
    if (!n) return toast.error("Enter an amount.");
    if (reason.trim().length < 3) return toast.error("Add a reason (at least 3 characters).");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/credits`, { amount: sign * n, reason: reason.trim() });
      toast.success(`${sign > 0 ? "Added" : "Removed"} ${n} credits · new balance ${data.balance}`);
      setAmount(""); setReason(""); onDone();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid="credit-adjust-form">
      <div className="flex gap-2">
        <input data-testid="credit-adjust-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Credits" className={`${inputCls} w-28`} />
        <input data-testid="credit-adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className={`${inputCls} flex-1`} />
      </div>
      <div className="flex gap-2">
        <button data-testid="credit-add-btn" disabled={busy} onClick={() => submit(1)} className={`${btnPrimary} flex-1`} style={gradient}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add</button>
        <button data-testid="credit-remove-btn" disabled={busy} onClick={() => submit(-1)} className="flex-1 rounded-full px-4 py-2 text-sm border border-red-400/40 text-red-300 hover:bg-red-500/10 flex items-center justify-center gap-1.5 disabled:opacity-50"><Minus className="w-4 h-4" /> Remove</button>
      </div>
    </div>
  );
}

export function BlockButton({ user, onDone }) {
  const [busy, setBusy] = useState(false);
  if (user.role === "admin") return null;
  const toggle = async () => {
    let reason = "";
    if (!user.blocked) {
      reason = window.prompt(`Block ${user.email}? They won't be able to generate anything or spend credits.\nReason (optional):`, "");
      if (reason === null) return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/block`, { blocked: !user.blocked, reason });
      toast.success(user.blocked ? `Unblocked ${user.email}` : `Blocked ${user.email}`); onDone();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={user.blocked ? "unblock-user-btn" : "block-user-btn"} disabled={busy} onClick={toggle}
      className={`rounded-full px-3 py-1.5 text-xs border flex items-center gap-1 disabled:opacity-50 ${user.blocked ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10" : "border-red-400/40 text-red-300 hover:bg-red-500/10"}`}>
      {user.blocked ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />} {user.blocked ? "Unblock" : "Block"}
    </button>
  );
}

export function UserRow({ user, open, onToggle, onDone, showAdjust = true }) {
  return (
    <Card data-testid="admin-user-row">
      <div className="flex items-start justify-between gap-3">
        <button className="text-left min-w-0 flex-1" onClick={onToggle} data-testid="admin-user-toggle">
          <div className="text-sm truncate" data-testid="admin-user-email">{user.email}</div>
          <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-1.5">
            {user.preferred_name || "—"} {user.role === "admin" && <Badge tone="cyan">admin</Badge>} {user.blocked && <Badge tone="red">blocked</Badge>}
          </div>
          {user.blocked && user.blocked_reason && <div className="text-[11px] text-red-300/80 mt-1">Reason: {user.blocked_reason}</div>}
        </button>
        <div className="text-right shrink-0">
          <div className="text-lg font-heading" data-testid="admin-user-balance">{user.credit_balance}</div>
          <div className="label-mono">credits</div>
        </div>
      </div>
      {open && (
        <>
          {showAdjust && <AdjustForm user={user} onDone={onDone} />}
          <div className="mt-3 flex justify-end"><BlockButton user={user} onDone={onDone} /></div>
        </>
      )}
    </Card>
  );
}

export function UsersSection() {
  const { q, setQ, users, reload } = useUsers();
  const [openId, setOpenId] = useState(null);
  return (
    <div className="space-y-3" data-testid="admin-users-section">
      <SearchBox value={q} onChange={setQ} testid="admin-user-search" />
      <div className="text-xs text-slate-500">Tap a user to add or remove credits. Every change needs a reason and is logged.</div>
      {users === null ? <Empty>Loading…</Empty> : users.length === 0 ? <Empty>No users match.</Empty> :
        users.map((u) => <UserRow key={u.id} user={u} open={openId === u.id} onToggle={() => setOpenId(openId === u.id ? null : u.id)} onDone={reload} />)}
    </div>
  );
}

export function BlockSection() {
  const { q, setQ, users, reload } = useUsers();
  const blocked = (users || []).filter((u) => u.blocked);
  const others = (users || []).filter((u) => !u.blocked && u.role !== "admin");
  return (
    <div className="space-y-4" data-testid="admin-block-section">
      <div className="text-sm text-slate-400 reading">A blocked user can still sign in and read, but can't generate anything or spend credits. You can unblock them at any time.</div>
      <div>
        <div className="label-mono mb-2">Blocked ({blocked.length})</div>
        <div className="space-y-2" data-testid="blocked-users-list">
          {users === null ? <Empty>Loading…</Empty> : blocked.length === 0 ? <Empty>No blocked users.</Empty> :
            blocked.map((u) => <UserRow key={u.id} user={u} open onToggle={() => {}} onDone={reload} showAdjust={false} />)}
        </div>
      </div>
      <div>
        <div className="label-mono mb-2">Block a user</div>
        <SearchBox value={q} onChange={setQ} testid="admin-block-search" />
        <div className="space-y-2 mt-2">
          {q.trim() ? others.slice(0, 20).map((u) => (
            <Card key={u.id} className="flex items-center justify-between gap-3 !py-3" data-testid="block-candidate-row">
              <div className="text-sm truncate">{u.email}</div><BlockButton user={u} onDone={reload} />
            </Card>
          )) : <div className="text-xs text-slate-500">Search to find a user to block.</div>}
        </div>
      </div>
    </div>
  );
}
